<template>
  <section>
    <aside>
      <div
        v-if="showLabel"
        :class="[
          'flex justify-between items-center py-2 text-sm text-[#344054] font-medium',
          classString,
        ]"
      >
        <label :for="id">{{ label }}</label>
        <div class="ml-auto">
          <slot name="otherDateFields"></slot>
        </div>
      </div>
      <div class="relative w-full">
        <input
          ref="dateInput"
          :id="id"
          :value="value"
          :class="[
            'input-datetime-' + id,
            'input',
            { 'border !border-red-500 rounded-lg': error },
            { 'border !border-gray-300': showBorder },
          ]"
          :style="{ height }"
          type="text"
          :placeholder="showTime ? 'dd.mm.yyyy, hh:mm' : 'dd.mm.yyyy'"
          @change="updateValue"
          :disabled="disabled"
          autocomplete="off"
        />
        <div
          v-show="showIcon"
          class="absolute mr-2 transform right-0 -translate-y-1/2 item-center top-1/2"
          style="cursor: pointer"
          @click="!disabled && fp && fp.open()"
        >
          <v-icon
            :icon="error ? 'custom:errorIcon' : 'custom:calendarIcon'"
            color="#98A2B3"
            size="20"
            :class="{ '!text-slate-400': disabled }"
          ></v-icon>
        </div>
      </div>
      <p v-if="error" class="text-red-500 text-[14px]">
        {{ $t(errorMessage.getTranslationKey()) }}
      </p>
    </aside>
  </section>
</template>
<script>
import flatpickr from "flatpickr";
import moment from "moment";

export default {
  props: {
    id: {
      type: String,
      default: "app-date-field",
    },
    showLabel: {
      type: Boolean,
      default: true,
    },
    label: {
      type: String,
      default: "",
    },
    showTime: {
      type: Boolean,
      default: false,
    },
    minDate: {
      type: String,
      default: "",
    },
    maxDate: {
      type: String,
      default: "",
    },
    errorMessage: {
      type: String,
      default: "This field is required",
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    isRequired: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    classString: {
      type: String,
      default: "",
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: String,
      default: "",
    },
    getTime: {
      type: String,
      default: "",
    },
    height: {
      type: String,
      default: "44px",
    },
    showIcon: {
      type: Boolean,
      default: true,
    },
    showBorder: {
      type: Boolean,
      default: true,
    },
    enableDate: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      value: "",
      error: false,
      fp: null,
    };
  },
  watch: {
    hasError(val) {
      this.error = val;
    },
    value(val) {
      if (val) {
        this.error = false;
      }
    },
    getTime(val) {
      const momentFormat = moment(this.modelValue, "YYYY-MM-DD").format(
        "DD.MM.YYYY"
      );
      this.value = this.showTime ? momentFormat + " " + val : momentFormat;
    },
    modelValue: {
      handler(val) {
        const momentFormat = val
          ? moment(val, "YYYY-MM-DD").format("DD.MM.YYYY")
          : "";
        const time = this.getTime ? this.getTime : "";
        this.value = this.showTime ? `${momentFormat} ${time}` : momentFormat;
      },
      immediate: true,
    },
    maxDate(val) {
      if (this.fp && typeof this.fp.set === "function") {
        // Check if date is already in DD.MM.YYYY format (contains dots)
        const formattedMaxDate = val.includes(".")
          ? val
          : moment(val, "YYYY-MM-DD").format("DD.MM.YYYY");
        console.log("formattedMaxDate:::", formattedMaxDate);
        this.fp.set("maxDate", formattedMaxDate);
      }
    },
    minDate(val) {
      if (this.fp && typeof this.fp.set === "function") {
        // Check if date is already in DD.MM.YYYY format (contains dots)
        const formattedMinDate = val.includes(".")
          ? val
          : moment(val, "YYYY-MM-DD").format("DD.MM.YYYY");
        this.fp.set("minDate", formattedMinDate);
      }
    },
    readOnly(val) {
      if (this.fp && typeof this.fp.set === "function") {
        this.fp.set("noCalendar", val);
      }
    },
  },
  methods: {
    parseDate(dateString) {
      // Handle 2-digit year inputs
      // Split the date string by dots
      const parts = dateString.split(".");

      if (parts.length === 3) {
        const day = parts[0];
        const month = parts[1];
        let year = parts[2];

        // If year is 2 digits, convert to 4 digits
        if (year.length === 2) {
          const yearNum = parseInt(year, 10);
          // Years 00-29 become 2000-2029
          // Years 30-99 become 1930-1999
          if (yearNum >= 0 && yearNum <= 29) {
            year = "20" + year;
          } else if (yearNum >= 30 && yearNum <= 99) {
            year = "19" + year;
          }
        }

        return `${day}.${month}.${year}`;
      }

      return dateString;
    },

    updateValue(event) {
      const date = event.target.value.split(" ")[0];
      const time = event.target.value.split(" ")[1];

      const momentFormat = date
        ? moment(date, "DD.MM.YYYY").format("YYYY-MM-DD")
        : "";

      this.$emit("update:modelValue", momentFormat);

      if (this.showTime && time) {
        this.$emit("updateTime", time);
      }
    },
  },
  mounted() {
    const inputElement = this.$refs.dateInput;
    if (inputElement) {
      this.fp = flatpickr(inputElement, {
        enableTime: this.showTime,
        dateFormat: this.showTime ? "d.m.Y H:i" : "d.m.Y",
        time_24hr: this.showTime,
        minDate: this.minDate
          ? this.minDate.includes(".")
            ? this.minDate
            : moment(this.minDate, "YYYY-MM-DD").format("DD.MM.YYYY")
          : null,
        maxDate: this.maxDate
          ? this.maxDate.includes(".")
            ? this.maxDate
            : moment(this.maxDate, "YYYY-MM-DD").format("DD.MM.YYYY")
          : null,
        allowInput: true,
        // clickOpens: false,
        noCalendar: this.readOnly,
        altFormat: "F j, Y",
        // enable: this.enableDate
        parseDate: (datestr) => {
          // Custom parser to handle 2-digit years
          const parsedDate = this.parseDate(datestr);
          return moment(parsedDate, "DD.MM.YYYY").toDate();
        },
      });
    }
  },
  beforeUnmount() {
    if (this.fp && typeof this.fp.destroy === "function") {
      this.fp.destroy();
    }
    this.fp = null;
  },
};
</script>
<style lang="css" scoped>
label {
  display: flex;
  flex-direction: column;
  font-size: 14px;
  font-weight: 500;
  color: #344054;
}
.input {
  align-items: center;
  padding: 10px 14px;
  outline-width: 0;
  width: 100%;
  height: 44px;
  background: #ffffff;
  color: #101828;

  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  border-radius: 8px;
}
input:focus {
  outline: none;
}
.focused {
  background: #ffffff;
  border: 1px solid #fdc6c2;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05),
  0 0 0 4px rgba(139, 41, 35, 0.04);
  border-radius: 8px;
}
input:disabled {
  color: #667085;
  background: #f9fafb;
  cursor: default;
}
.disabled {
  background-color: #ae6a65;
}
</style>
